// ApiKeys.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Toast
import { toast } from 'react-toastify';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import ApiKeyWizard from './ApiKeyWizard';
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';

// Services
import {
    deleteWorkspaceServiceAccount,
    listWorkspaceServiceAccounts,
    revokeWorkspaceServiceAccount,
    rotateWorkspaceServiceAccountToken,
} from 'services/api/ApiAccessService';

// Utilities
import { cleanString, downloadFile } from '../apiUtils';

// CSS
import './ApiKeys.css';

const API_KEY_WRITE_SCOPE_SET = new Set([
    'node.create',
    'node.updateOwn',
    'node.updateAny',
    'node.deleteOwn',
    'node.deleteAny',
    'collection.create',
    'collection.updateOwn',
    'collection.delete',
    'types.create',
    'types.update',
    'types.delete',
    'dashboard.write',
    'module.write',
    'relationPreset.write',
    'api.credentials.manage',
]);

function normalizeTypePermissionLevel(value) {
    const normalized = cleanString(value).toLowerCase();
    if (normalized === 'read' || normalized === 'write') {
        return normalized;
    }
    return 'none';
}

function resolveTypeRows(schemaTypes) {
    const rows = Array.isArray(schemaTypes) ? schemaTypes : [];

    return rows
        .map((row) => ({
            typeId: cleanString(row?.typeId || row?.id || row?.key).toLowerCase(),
            label: cleanString(row?.label || row?.name || row?.title),
        }))
        .filter((row) => row.typeId)
        .sort((a, b) => a.label.localeCompare(b.label));
}

function inferApiKeyAccessLevel(account) {
    const permissionMap = account?.permissions?.types && typeof account.permissions.types === 'object'
        ? account.permissions.types
        : {};

    const hasWritePermission = Object.values(permissionMap).some((level) => normalizeTypePermissionLevel(level) === 'write');
    if (hasWritePermission) return 'View and change data';

    const hasReadPermission = Object.values(permissionMap).some((level) => normalizeTypePermissionLevel(level) === 'read');
    if (hasReadPermission) return 'View only';

    const scopes = Array.isArray(account?.scopes) ? account.scopes : [];
    const hasWriteScope = scopes.some((scope) => API_KEY_WRITE_SCOPE_SET.has(cleanString(scope)));
    if (hasWriteScope) return 'View and change data';

    if (scopes.length > 0) return 'View only';

    return 'No data access';
}

function summarizeTypePermissions(typePermissionMap, schemaTypes) {
    const rows = resolveTypeRows(schemaTypes);
    const permissionMap = typePermissionMap && typeof typePermissionMap === 'object' ? typePermissionMap : {};

    let writeCount = 0;
    let readCount = 0;
    let noneCount = 0;

    rows.forEach((row) => {
        const level = normalizeTypePermissionLevel(permissionMap[row.typeId]);
        if (level === 'write') {
            writeCount += 1;
            return;
        }
        if (level === 'read') {
            readCount += 1;
            return;
        }
        noneCount += 1;
    });

    return {
        writeCount,
        readCount,
        noneCount,
        compact: `${writeCount} write • ${readCount} read • ${noneCount} none`,
    };
}

/**
 * ApiKeys
 *
 * Renders API key onboarding and creation flow for the API page.
 *
 * @param {string} workspaceId - Active workspace id.
 * @param {string} workspaceRole - Current user role in workspace.
 * @param {Array} schemaTypes - Workspace type rows.
 *
 * @returns {ReactNode} ApiKeys.
 */
const ApiKeys = React.memo(({
    workspaceId,
    workspaceRole,
    schemaTypes,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- FLAGS ----- //

    const canManageCredentials = workspaceRole === 'owner' || workspaceRole === 'admin';

    // ----- MEMOIZED VALUES ----- //

    const typeRows = useMemo(() => resolveTypeRows(schemaTypes), [schemaTypes]);
    // ----- STATE ----- //

    const [loading, setLoading] = useState(false);
    const [serviceAccounts, setServiceAccounts] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [busyActionKey, setBusyActionKey] = useState('');

    const [apiKeyWizardOpen, setApiKeyWizardOpen] = useState(false);

    const [revealedToken, setRevealedToken] = useState('');
    const [revealedTokenAccountId, setRevealedTokenAccountId] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [pendingDeleteAccount, setPendingDeleteAccount] = useState(null);

    const sortedServiceAccounts = useMemo(() => {
        return [...serviceAccounts].sort((a, b) => {
            const aDate = new Date(a?.lastUsedAt || a?.createdAt || 0).getTime();
            const bDate = new Date(b?.lastUsedAt || b?.createdAt || 0).getTime();
            return bDate - aDate;
        });
    }, [serviceAccounts]);

    const deleteConfirmMessage = useMemo(() => {
        const accountName = cleanString(pendingDeleteAccount?.name) || 'this API key';
        const status = cleanString(pendingDeleteAccount?.status || 'active').toLowerCase();

        if (status === 'revoked') {
            return `Delete revoked API key "${accountName}" permanently? This cannot be undone.`;
        }

        return `Delete API key "${accountName}" permanently? This immediately removes it and cannot be undone.`;
    }, [pendingDeleteAccount]);

    // ----- HANDLERS ----- //

    const loadApiKeys = useCallback(async () => {
        if (!workspaceId) return;

        setLoading(true);
        setErrorMessage('');

        try {
            if (!canManageCredentials) {
                setServiceAccounts([]);
                return;
            }

            const response = await listWorkspaceServiceAccounts({ workspaceId });
            const rows = Array.isArray(response?.data) ? response.data : [];
            setServiceAccounts(rows);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to load API keys.');
        } finally {
            setLoading(false);
        }
    }, [canManageCredentials, workspaceId]);

    useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);

    const setTokenReveal = useCallback((serviceAccountId, token) => {
        const cleanToken = cleanString(token);
        if (!cleanToken) return;

        setRevealedToken(cleanToken);
        setRevealedTokenAccountId(cleanString(serviceAccountId));
    }, []);

    const handleStartApiKeyFlow = useCallback(() => {
        if (!canManageCredentials) return;
        setErrorMessage('');
        setStatusMessage('');
        setApiKeyWizardOpen(true);
    }, [canManageCredentials]);

    const handleApiKeyWizardCreated = useCallback(async () => {
        await loadApiKeys();
    }, [loadApiKeys]);

    const handleDismissRevealedToken = useCallback(() => {
        setRevealedToken('');
        setRevealedTokenAccountId('');
    }, []);

    const handleRotateApiKey = useCallback(async (serviceAccountId) => {
        if (!workspaceId || !serviceAccountId || !canManageCredentials) return;
        if (!window.confirm('Rotate this API key now? Existing integrations must update to the new key.')) return;

        setBusyActionKey(`rotate:${serviceAccountId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            const response = await rotateWorkspaceServiceAccountToken({ workspaceId, serviceAccountId });
            const token = cleanString(response?.token);
            setTokenReveal(serviceAccountId, token);

            setStatusMessage('API key rotated. Save the new key before leaving this screen.');
            toast.success('API key rotated.');
            await loadApiKeys();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to rotate API key.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, loadApiKeys, setTokenReveal, workspaceId]);

    const handleRevokeApiKey = useCallback(async (serviceAccountId) => {
        if (!workspaceId || !serviceAccountId || !canManageCredentials) return;
        if (!window.confirm('Revoke this API key? This immediately blocks API access for this key.')) return;

        setBusyActionKey(`revoke:${serviceAccountId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await revokeWorkspaceServiceAccount({ workspaceId, serviceAccountId });
            if (cleanString(revealedTokenAccountId) === cleanString(serviceAccountId)) {
                setRevealedToken('');
                setRevealedTokenAccountId('');
            }
            setStatusMessage('API key revoked.');
            toast.success('API key revoked.');
            await loadApiKeys();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to revoke API key.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, loadApiKeys, revealedTokenAccountId, workspaceId]);

    const handleDeleteApiKey = useCallback((serviceAccountId, status, name) => {
        if (!workspaceId || !serviceAccountId || !canManageCredentials) return;

        setPendingDeleteAccount({
            serviceAccountId: cleanString(serviceAccountId),
            status: cleanString(status || 'active').toLowerCase(),
            name: cleanString(name || serviceAccountId),
        });
        setDeleteConfirmOpen(true);
    }, [canManageCredentials, workspaceId]);

    const handleSetDeleteConfirmOpen = useCallback((nextOpen) => {
        setDeleteConfirmOpen(Boolean(nextOpen));
        if (!nextOpen) {
            setPendingDeleteAccount(null);
        }
    }, []);

    const handleConfirmDeleteApiKey = useCallback(async () => {
        const serviceAccountId = cleanString(pendingDeleteAccount?.serviceAccountId);
        if (!workspaceId || !serviceAccountId || !canManageCredentials) return;

        setDeleteConfirmOpen(false);

        setBusyActionKey(`delete:${serviceAccountId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await deleteWorkspaceServiceAccount({ workspaceId, serviceAccountId });
            if (cleanString(revealedTokenAccountId) === cleanString(serviceAccountId)) {
                setRevealedToken('');
                setRevealedTokenAccountId('');
            }
            setStatusMessage('API key deleted.');
            toast.success('API key deleted.');
            await loadApiKeys();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to delete API key.');
        } finally {
            setBusyActionKey('');
            setPendingDeleteAccount(null);
        }
    }, [canManageCredentials, loadApiKeys, pendingDeleteAccount, revealedTokenAccountId, workspaceId]);

    const handleCopyRevealedToken = useCallback(async () => {
        if (!revealedToken) return;

        try {
            await navigator.clipboard.writeText(revealedToken);
            toast.success('API key copied.');
        } catch (error) {
            toast.error(error?.message || 'Could not copy API key.');
        }
    }, [revealedToken]);

    const handleDownloadRevealedToken = useCallback(() => {
        if (!revealedToken || !revealedTokenAccountId) return;

        const fileName = `workspace-${workspaceId}-api-key-${revealedTokenAccountId}.txt`;
        downloadFile(fileName, `${revealedToken}\n`, 'text/plain;charset=utf-8');
    }, [revealedToken, revealedTokenAccountId, workspaceId]);

    // ----- RENDER ----- //

    if (!workspaceId) {
        return (
            <div className="api-keys-note" style={{ color: theme.foregroundMuted }}>
                Select an active workspace to manage API keys.
            </div>
        );
    }

    return (
        <div className="api-keys-container">
            {!canManageCredentials && (
                <div className="api-keys-note" style={{ color: theme.foregroundMuted }}>
                    Read-only mode for this user. Owners and admins can create and manage API keys.
                </div>
            )}

            {errorMessage && (
                <div className="api-keys-error">{errorMessage}</div>
            )}

            {statusMessage && !errorMessage && (
                <div className="api-keys-status">{statusMessage}</div>
            )}

            <section className="api-keys-card"
                style={{ 
                    borderColor: theme.backgroundMuted, 
                    backgroundColor: theme.panel 
                }}>
                <div className="api-keys-title" style={{ color: theme.foreground }}>API Keys</div>
                <div className="api-keys-subtitle" style={{ color: theme.foregroundMuted }}>
                    Create one API key per integration and set access by type.
                </div>
                <div className="api-keys-subtitle" style={{ color: theme.foregroundMuted }}>
                    Any type not granted stays blocked by default.
                </div>

                <div className="api-keys-actions">
                    <Button
                        iconKey="person_add"
                        label="+ Add API Key"
                        onClick={handleStartApiKeyFlow}
                        enabled={canManageCredentials && !loading}
                    />
                </div>

                <div className="api-keys-list">
                    {sortedServiceAccounts.length === 0 && (
                        <div className="api-keys-empty" style={{ color: theme.foregroundMuted }}>
                            {loading ? 'Loading API keys...' : 'No API keys yet.'}
                        </div>
                    )}

                    {sortedServiceAccounts.map((account) => {
                        const accountId = account?.id || account?.key;
                        const accountStatus = cleanString(account?.status || 'active').toLowerCase();
                        const accessLabel = inferApiKeyAccessLevel(account);
                        const isRevoked = accountStatus === 'revoked';
                        const summary = summarizeTypePermissions(account?.permissions?.types, typeRows);

                        return (
                            <div key={accountId} className="api-keys-row" style={{ borderColor: theme.backgroundMuted }}>
                                <div className="api-keys-row-meta">
                                    <div className="api-keys-row-title" style={{ color: theme.foreground }}>
                                        {account?.name || ''}
                                    </div>
                                    <div className="api-keys-row-subtitle" style={{ color: theme.foregroundMuted }}>
                                        access: {accessLabel} • status: {account?.status || 'active'}
                                    </div>
                                    <div className="api-keys-row-subtitle" style={{ color: theme.foregroundMuted }}>
                                        type permissions: {summary.compact}
                                    </div>
                                </div>

                                <div className="api-keys-actions">
                                    <Button
                                        label="Rotate"
                                        onClick={() => handleRotateApiKey(accountId)}
                                        enabled={canManageCredentials && !isRevoked && busyActionKey !== `rotate:${accountId}`}
                                    />

                                    <Button
                                        label="Revoke"
                                        onClick={() => handleRevokeApiKey(accountId)}
                                        enabled={canManageCredentials && !isRevoked && busyActionKey !== `revoke:${accountId}`}
                                        foregroundColor={theme.warning}
                                        borderColor={theme.warning}
                                    />

                                    <Button
                                        label="Delete"
                                        onClick={() => handleDeleteApiKey(accountId, accountStatus, account?.name || '')}
                                        enabled={canManageCredentials && busyActionKey !== `delete:${accountId}`}
                                        height="30px"
                                        fontSize="12px"
                                        borderRadius="6px"
                                        foregroundColor={theme.warning}
                                        borderColor={theme.warning}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

            </section>

            <ApiKeyWizard
                isOpen={apiKeyWizardOpen}
                setOpen={setApiKeyWizardOpen}
                workspaceId={workspaceId}
                canManageCredentials={canManageCredentials}
                typeRows={typeRows}
                onCreated={handleApiKeyWizardCreated}
            />

            <Confirm
                title="Delete API Key"
                message={deleteConfirmMessage}
                isOpen={deleteConfirmOpen}
                setOpen={handleSetDeleteConfirmOpen}
                onConfirm={handleConfirmDeleteApiKey}
            />

            {Boolean(revealedToken) && (
                <section className="api-keys-card"
                style={{ 
                    borderColor: theme.backgroundMuted, 
                    backgroundColor: theme.panel 
                }}>
                    <div className="api-keys-title" style={{ color: theme.foreground }}>Save your API key (shown once)</div>

                    {revealedToken ? (
                        <>
                            <textarea
                                className="api-keys-token"
                                value={revealedToken}
                                readOnly
                                style={{
                                    color: theme.foreground,
                                    backgroundColor: theme.input,
                                    borderColor: theme.backgroundMuted,
                                }}
                            />

                            <div className="api-keys-actions">
                                <Button
                                    label="Copy API Key"
                                    iconKey="content_copy"
                                    onClick={handleCopyRevealedToken}
                                />

                                <Button
                                    label="Download API Key"
                                    iconKey="download"
                                    onClick={handleDownloadRevealedToken}
                                />
                            </div>

                            <div className="api-keys-subtitle" style={{ color: theme.foregroundMuted }}>
                                You cannot view this full API key again later.
                            </div>

                            <div className="api-keys-actions">
                                <Button
                                    label="Done"
                                    onClick={handleDismissRevealedToken}
                                    enabled={true}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="api-keys-empty" style={{ color: theme.foregroundMuted }}>
                            Could not reveal API key. Please try creating it again.
                        </div>
                    )}
                </section>
            )}
        </div>
    );
});

// Dev Tools
ApiKeys.displayName = 'ApiKeys';

export default ApiKeys;
